import React from 'react';
import PropTypes from 'prop-types';

import {
  HOUSEHOLD_APPLICATION_STATUS_REQUESTED,
  HOUSEHOLD_APPLICATION_STATUS_STARTED,
  HOUSEHOLD_APPLICATION_STATUS_COMPLETED,
  HOUSEHOLD_APPLICATION_STATUS_PAID,
  HOUSEHOLD_APPLICATION_STATUS_INCOME_VERIFIED,
  HOUSEHOLD_APPLICATION_STATUS_PENDING_DOCUMENTS,
  HOUSEHOLD_APPLICATION_STATUS_PROCESSING,
  HOUSEHOLD_APPLICATION_STATUS_PENDING_INTERNAL_REVIEW,
  HOUSEHOLD_APPLICATION_STATUS_DELIVERED,
  HOUSEHOLD_APPLICATION_STATUS_PENDING_LANDLORD,
  HOUSEHOLD_APPLICATION_STATUS_ACCEPTED,
  HOUSEHOLD_APPLICATION_STATUS_DEPOSIT_PAID,
  HOUSEHOLD_APPLICATION_STATUS_LEASE_DRAFT_PENDING,
  HOUSEHOLD_APPLICATION_STATUS_LEASE_SENT,
  HOUSEHOLD_APPLICATION_STATUS_LEASE_SIGNED,
  HOUSEHOLD_APPLICATION_STATUS_PARENT_GROUP_TO_CHILDREN_MAPPING,
} from 'common/householdConstants';

import { DATA_TABLE_SOURCE_HOUSEHOLD_KANBAN_APPLICATION_COLUMN } from 'common/dataTableConstants';

import SrDataTableConnected from 'client/components/SrDataTableConnected';
import SrHouseholdKanbanBoardColumn from 'client/components/SrHouseholdKanbanBoardColumn';

const mainHouseholdApplicationStatuList = [
  HOUSEHOLD_APPLICATION_STATUS_REQUESTED,
  HOUSEHOLD_APPLICATION_STATUS_STARTED,
  HOUSEHOLD_APPLICATION_STATUS_COMPLETED,
  HOUSEHOLD_APPLICATION_STATUS_PAID,
];

const mainHouseholdApplicationStatuListReversed = [
  HOUSEHOLD_APPLICATION_STATUS_PAID,
  HOUSEHOLD_APPLICATION_STATUS_COMPLETED,
  HOUSEHOLD_APPLICATION_STATUS_STARTED,
  HOUSEHOLD_APPLICATION_STATUS_REQUESTED,
];

const getDataTableSettingsFromProps = ({
  moveInDateRanges,
  listingManagementCompany,
  listingServices,
  leasingManagerUserId,
  applicationStatus,
}) => {
  const dataTableSettings = {
    itemsPropName: 'households',
    dataSource: DATA_TABLE_SOURCE_HOUSEHOLD_KANBAN_APPLICATION_COLUMN,
    rootViewId: DATA_TABLE_SOURCE_HOUSEHOLD_KANBAN_APPLICATION_COLUMN,
    initialViewSettings: {
      loadAllData: true,
      customParams: {
        householdKanbanApplicationColumn: {
          moveInDateRanges,
          listingManagementCompany,
          listingServices,
          leasingManagerUserId,
          includeHouseholdApplicationStatusList: [
            HOUSEHOLD_APPLICATION_STATUS_REQUESTED,
            HOUSEHOLD_APPLICATION_STATUS_STARTED,
            HOUSEHOLD_APPLICATION_STATUS_COMPLETED,
            HOUSEHOLD_APPLICATION_STATUS_PAID,
            HOUSEHOLD_APPLICATION_STATUS_INCOME_VERIFIED,
            HOUSEHOLD_APPLICATION_STATUS_PENDING_DOCUMENTS,
            HOUSEHOLD_APPLICATION_STATUS_PROCESSING,
            HOUSEHOLD_APPLICATION_STATUS_PENDING_INTERNAL_REVIEW,
          ],
          excludeHouseholdApplicationStatusList: [
            HOUSEHOLD_APPLICATION_STATUS_DELIVERED,
            HOUSEHOLD_APPLICATION_STATUS_PENDING_LANDLORD,
            HOUSEHOLD_APPLICATION_STATUS_ACCEPTED,
            HOUSEHOLD_APPLICATION_STATUS_DEPOSIT_PAID,
            HOUSEHOLD_APPLICATION_STATUS_LEASE_DRAFT_PENDING,
            HOUSEHOLD_APPLICATION_STATUS_LEASE_SENT,
            HOUSEHOLD_APPLICATION_STATUS_LEASE_SIGNED,
          ],
          applicationStatus,
          householdListingStatus: 'Active',
        },
      },
    },
  };

  return dataTableSettings;
};

const areHouseholdAppsInStatus = ({
  household,
  householdApplicationStatus,
  householdApplicationStatusList,
}) => {
  // For the paid we only need all apps paid. WTA or started apps should move to their column.
  const fn = householdApplicationStatus === HOUSEHOLD_APPLICATION_STATUS_PAID ? 'every' : 'some';
  return household.listingApplications.some(({ applications }) => {
    return applications[fn]((app) => householdApplicationStatusList.includes(app.status));
  });
};

export const sortPaidColumn = ({ households }) => {
  const lastAppUpdatedDateByHousehold = households.reduce((memo, household) => {
    // Recent updated apps at the top
    const appDates = household.listingApplications
      .reduce((memo, { applications }) => {
        return [
          ...memo,
          ...applications.map(({ lastUpdatedDate }) => new Date(lastUpdatedDate).getTime()),
        ];
      }, [])
      .sort((a, b) => b - a);

    memo[household.id] = appDates[0];

    return memo;
  }, {});

  households.sort(
    (a, b) => lastAppUpdatedDateByHousehold[b.id] - lastAppUpdatedDateByHousehold[a.id],
  );
};

export const makeColumns = ({ households }) => {
  const columns = mainHouseholdApplicationStatuList.reduce(
    (memo, status) => ({
      ...memo,
      [status]: {
        households: [],
        count: 0,
      },
    }),
    {},
  );

  for (const household of households) {
    for (const status of mainHouseholdApplicationStatuListReversed) {
      const isTrue = areHouseholdAppsInStatus({
        household,
        householdApplicationStatus: status,
        householdApplicationStatusList:
          HOUSEHOLD_APPLICATION_STATUS_PARENT_GROUP_TO_CHILDREN_MAPPING[status],
      });
      if (isTrue) {
        columns[status].households.push(household);
        break;
      }
    }
  }

  sortPaidColumn({ households: columns[HOUSEHOLD_APPLICATION_STATUS_PAID].households });

  return columns;
};

const SrHouseholdKanbanBoard = ({
  moveInDateRanges,
  listingManagementCompany,
  listingServices,
  accountManagerUserId,
  leasingManagerUserId,
  listingIdsWithAppsInReadyForLease,
}) => {
  const dataTableSettingsFromProps = getDataTableSettingsFromProps({
    moveInDateRanges,
    listingManagementCompany,
    listingServices,
    leasingManagerUserId,
  });

  return (
    <SrDataTableConnected {...dataTableSettingsFromProps}>
      {(activeHouseholdsDataTable) => {
        const columns = makeColumns({
          households: activeHouseholdsDataTable.households,
        });

        return mainHouseholdApplicationStatuList.map((householdApplicationStatus) => (
          <SrHouseholdKanbanBoardColumn
            key={householdApplicationStatus}
            moveInDateRanges={moveInDateRanges}
            listingManagementCompany={listingManagementCompany}
            listingServices={listingServices}
            accountManagerUserId={accountManagerUserId}
            leasingManagerUserId={leasingManagerUserId}
            households={columns[householdApplicationStatus].households}
            allHouseholds={activeHouseholdsDataTable.households}
            householdApplicationStatus={householdApplicationStatus}
            inProgress={activeHouseholdsDataTable.dataTableApiState.inProgress}
            listingIdsWithAppsInReadyForLease={listingIdsWithAppsInReadyForLease}
          />
        ));
      }}
    </SrDataTableConnected>
  );
};

SrHouseholdKanbanBoard.propTypes = {
  moveInDateRanges: PropTypes.array,
  listingManagementCompany: PropTypes.array,
  accountManagerUserId: PropTypes.array,
};

SrHouseholdKanbanBoard.displayName = 'SrHouseholdKanbanBoard';

export default SrHouseholdKanbanBoard;
